import type { Ref, ToRefs } from 'vue'
import { reactive, toRefs, watch, ref } from 'vue'

import type { EditDeck } from '~/composables/useEditDeck'
import type { ApiListView } from '@/entities/api'
import type { DeckAccessEntry, UserWithUIState } from '@/entities/user'
import { sortBy, uniqBy } from 'ramda'

interface State {
  admins: UserWithUIState[]
  nonAdmins: UserWithUIState[]
  loading: boolean
}

export interface UseEditSecureAccess extends ToRefs<State> {
  grantViewAccess(user: UserWithUIState): void
  revokeViewAccess(user: UserWithUIState): void
  grantEditAccess(user: UserWithUIState): void
  revokeEditAccess(user: UserWithUIState): void
  addNewUser(email: string): Promise<DeckAccessCreated | null>
}

interface DeckAccessCreated {
  resource_id: number
  user_id: number
  access_type: 'view' | 'edit' | 'none'
}

export function useEditSecureAccess({
  deck,
  onError,
  loadData = true,
}: {
  deck: Ref<EditDeck | null>
  onError?(error: string): void
  loadData?: boolean
}): UseEditSecureAccess {
  const deckId = ref<number | undefined>()
  const state = reactive<State>({
    admins: [],
    nonAdmins: [],
    loading: false,
  })

  if (loadData) {
    watch(
      deck,
      async deck => {
        if (deck && deck.id !== deckId.value) {
          await _refresh()
          deckId.value = deck.id
        }
      },
      { immediate: true }
    )
  }

  async function grantViewAccess(nonAdmin: UserWithUIState) {
    if (!deck.value) return
    try {
      await $fetch(`/v2/access/view/deck/${deck.value.id}/${nonAdmin.email_address}`, {
        ...useFetchSharedOptions(),
        method: 'POST',
      })
      nonAdmin.has_view_access = true
    } catch (e: any) {
      const error = 'response' in e ? e.response.data[0] : e.message
      onError?.(error)
    }
  }

  async function revokeViewAccess(nonAdmin: UserWithUIState) {
    if (!deck.value) return
    try {
      await $fetch(`/v2/access/none/deck/${deck.value.id}/${nonAdmin.email_address}`, {
        ...useFetchSharedOptions(),
        method: 'POST',
      })
      nonAdmin.has_view_access = false
    } catch (e: any) {
      const error = 'response' in e ? e.response.data[0] : e.message
      onError?.(error)
    }
  }

  async function grantEditAccess(admin: UserWithUIState): Promise<void> {
    if (!deck.value) return
    try {
      await $fetch(`/v2/access/edit/deck/${deck.value.id}/${admin.id}`, {
        ...useFetchSharedOptions(),
        method: 'POST',
      })
      admin.has_edit_access = true
    } catch (e: any) {
      const error = 'response' in e ? e.response.data[0] : e.message
      onError?.(error)
    }
  }
  async function revokeEditAccess(admin: UserWithUIState): Promise<void> {
    if (!deck.value) return
    try {
      await $fetch(`/v2/access/edit/deck/${deck.value.id}/${admin.id}`, {
        ...useFetchSharedOptions(),
        method: 'DELETE',
      })
      admin.has_edit_access = false
    } catch (e: any) {
      const error = 'response' in e ? e.response.data[0] : e.message
      onError?.(error)
    }
  }

  async function addNewUser(email: string): Promise<DeckAccessCreated | null> {
    if (!deck.value) return null
    try {
      const data = await $fetch<DeckAccessCreated>(`/v2/access/none/deck/${deck.value.id}/${email}`, {
        ...useFetchSharedOptions(),
        method: 'POST',
      })
      await _refresh()
      return data
    } catch (e: any) {
      const error = 'response' in e ? e.response.data[0] : e.message
      onError?.(error)
      return null
    }
  }

  async function _refresh(): Promise<void> {
    if (!deck.value) return
    state.loading = true
    const [viewAccess, noneAccess, editAccess, { results: companyAdmins }] = await Promise.all([
      _getList('view'),
      _getList('none'),
      _getList('edit'),
      $fetch<any>(`/v2/companies/${deck.value.company_id}/admins`, {
        ...useFetchSharedOptions(),
        method: 'GET',
        params: { limit: 500 },
      }),
    ])

    state.nonAdmins = sortBy(
      x => x.name || '',
      uniqBy(
        x => x.id,
        [...viewAccess.map(u => ({ ...u, has_view_access: true })), ...noneAccess.map(u => ({ ...u, has_view_access: false }))]
      )
    )

    // We load the people that can edit first, because if they are in this array then
    // we need to go ahead and combine the lists and remove them from the viewers list.
    const editors = editAccess.map(u => u.id)

    // Pull the company admins and set the edit access state so that we can make sure
    // that the switches are defaulted in the right place.
    const admins: UserWithUIState[] = companyAdmins.map((u: UserWithUIState) => {
      u.has_edit_access = editors.includes(u.id)
      return u
    })

    // Order the admins so that we can stack anyone with edit access to the top
    // of the list for UX ease. This will float people with edit access to the top.
    state.admins = admins.sort(admin => (admin.has_edit_access ? -1 : 1))
    state.loading = false
  }

  async function _getList(accessType: 'view' | 'edit' | 'none'): Promise<UserWithUIState[]> {
    if (!deck.value) return []
    return (
      await $fetch<ApiListView<DeckAccessEntry>>(`/v2/access/${accessType}/deck/${deck.value.id}/users/`, {
        ...useFetchSharedOptions(),
        method: 'GET',
        params: { limit: 1000 },
      })
    ).results
  }

  return { ...toRefs(state), grantViewAccess, revokeViewAccess, grantEditAccess, revokeEditAccess, addNewUser }
}
